import { MapPin, Phone, Mail, MessageCircle, ExternalLink, Clock } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Contact() {
  const waLink = `https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
    "Hi Vinodha Diagnostics, I'd like to book an appointment."
  )}`;

  return (
    <section id="contact" data-testid="contact-section" className="bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#0369A1]">
              Visit us
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545]">
              Two centres across South Bangalore.
            </h2>
            <p className="mt-4 text-[#64748B] leading-relaxed">
              Walk in or call ahead — we serve patients from Electronic City, Hosa Road,
              Hebbagodi, Bommasandra, Singasandra, Begur and Bommanahalli.
            </p>

            <div className="mt-8 space-y-4">
              {SITE.branches.map((b, i) => (
                <div
                  key={b.name}
                  data-testid={`contact-branch-${i}`}
                  className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="grid place-items-center w-10 h-10 rounded-xl bg-[#F0F9FF] text-[#0369A1] shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold text-[#0B2545]">{b.name}</span>
                        <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#0369A1] bg-[#F0F9FF] px-2 py-0.5 rounded-full">
                          {b.label}
                        </span>
                      </div>
                      <p className="text-sm text-[#64748B] mt-1">{b.address}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                        {b.phones.map((ph) => (
                          <a
                            key={ph}
                            href={`tel:${ph.replace(/\s/g, "")}`}
                            className="inline-flex items-center gap-1.5 font-semibold text-[#0B2545] hover:text-[#0369A1]"
                          >
                            <Phone className="w-3.5 h-3.5" /> {ph}
                          </a>
                        ))}
                      </div>
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#64748B]">
                        <Clock className="w-3.5 h-3.5 text-[#0369A1]" />
                        {b.morning} · {b.evening}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-call-btn"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white font-semibold text-sm transition-colors"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold text-sm transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-btn"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full border-2 border-[#0B2545] text-[#0B2545] hover:bg-[#F8FAFC] font-semibold text-sm"
              >
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {SITE.branches.map((b, i) => (
              <div key={b.name} data-testid={`contact-map-wrap-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="inline-flex items-center gap-2">
                    <span className="font-display font-bold text-[#0B2545]">{b.name}</span>
                    <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#0369A1] bg-[#F0F9FF] px-2 py-0.5 rounded-full">
                      {b.label}
                    </span>
                  </div>
                  <a
                    href={b.mapLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-map-link-${i}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0369A1] hover:underline"
                  >
                    Open in Maps <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                  <iframe
                    title={`Vinodha Diagnostics — ${b.name} location`}
                    src={b.mapEmbed}
                    width="100%"
                    height="320"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid={`contact-map-${i}`}
                    className="w-full h-[260px] sm:h-[320px]"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
